/**
 * CashDrawerTab - Cash drawer/till management
 * Open/close shifts, track cash, drops, and payouts
 * Also shows POS staff cash drawer activity log
 */
import { useState, useEffect } from 'react';
import { 
  Wallet, DollarSign, ArrowUpRight, ArrowDownRight, 
  Clock, Check, AlertTriangle, Minus, Plus, RefreshCw,
  LockOpen, User, Filter, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function CashDrawerTab({ authHeaders }) {
  const [session, setSession] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // POS Activity Log State
  const [posDrawerLog, setPosDrawerLog] = useState([]);
  const [staffBreakdown, setStaffBreakdown] = useState([]);
  const [showPosLog, setShowPosLog] = useState(true);
  const [filterStaff, setFilterStaff] = useState('');
  
  // Form states
  const [openingFloat, setOpeningFloat] = useState('200');
  const [actualCash, setActualCash] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const [dropAmount, setDropAmount] = useState('');
  const [dropNotes, setDropNotes] = useState('');
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutDesc, setPayoutDesc] = useState('');

  useEffect(() => {
    fetchCurrentSession();
    fetchHistory();
    fetchSummary();
    fetchPosDrawerLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPosDrawerLog = async () => {
    try {
      const params = filterStaff 
        ? `staff_id=${filterStaff}&limit=50`
        : `limit=50`;
      
      const res = await api.get(`/admin/cash-drawer-log?${params}`);
      setPosDrawerLog(res.data.logs || []);
      setStaffBreakdown(res.data.staff_breakdown || []);
    } catch (err) {
      console.error('Failed to fetch POS drawer log:', err);
    }
  };

  useEffect(() => {
    fetchPosDrawerLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStaff]);

  const fetchCurrentSession = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/till/current');
      setSession(res.data.session);
      setIsOpen(res.data.is_open);
    } catch (err) {
      console.error('Failed to fetch session:', err);
    }
    setLoading(false);
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/admin/till/history?days=7');
      setHistory(res.data.sessions || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await api.get('/admin/till/summary?days=7');
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  const openTill = async () => {
    try {
      await api.post(`/admin/till/open?opening_float=${openingFloat}`);
      toast.success('Till opened!');
      fetchCurrentSession();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open till');
    }
  };

  const closeTill = async () => {
    if (!actualCash) {
      toast.error('Please enter the actual cash count');
      return;
    }
    
    try {
      const res = await api.post('/admin/till/close', {
        actual_cash: parseFloat(actualCash),
        notes: closeNotes
      });
      
      toast.success('Till closed!');
      setActualCash('');
      setCloseNotes('');
      fetchCurrentSession();
      fetchHistory();
      fetchSummary();
      
      // Show variance if any
      const variance = res.data.report?.variance || 0;
      if (Math.abs(variance) > 0.01) {
        toast[variance > 0 ? 'info' : 'warning'](
          `Variance: ${variance > 0 ? '+' : ''}$${variance.toFixed(2)} (${res.data.report?.variance_status})`
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close till');
    }
  };

  const recordDrop = async () => {
    if (!dropAmount) {
      toast.error('Please enter drop amount');
      return;
    }
    
    try {
      await api.post('/admin/till/drop', {
        amount: parseFloat(dropAmount),
        notes: dropNotes
      });
      
      toast.success('Cash drop recorded');
      setDropAmount('');
      setDropNotes('');
      fetchCurrentSession();
    } catch (err) {
      toast.error('Failed to record drop');
    }
  };

  const recordPayout = async () => {
    if (!payoutAmount || !payoutDesc) {
      toast.error('Please enter amount and description');
      return;
    }
    
    try {
      await api.post(`/admin/till/payout?amount=${payoutAmount}&description=${encodeURIComponent(payoutDesc)}`);
      toast.success('Payout recorded');
      setPayoutAmount('');
      setPayoutDesc('');
      fetchCurrentSession();
    } catch (err) {
      toast.error('Failed to record payout');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cash-drawer-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <Wallet className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Cash Drawer</h2>
            <p className="text-muted-foreground text-sm">
              {isOpen ? 'Till is open' : 'Till is closed'}
            </p>
          </div>
        </div>
        <div className={`px-4 py-2 rounded-full ${isOpen ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`}>
          {isOpen ? 'OPEN' : 'CLOSED'}
        </div>
      </div>

      {/* Current Session or Open Till */}
      {isOpen && session ? (
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-heading text-lg text-foreground mb-4">Current Session</h3>
          
          {/* Session Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="text-sm text-muted-foreground">Opening Float</div>
              <div className="text-xl font-bold text-foreground">${session.opening_float?.toFixed(2)}</div>
            </div>
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="text-sm text-muted-foreground">Cash Sales</div>
              <div className="text-xl font-bold text-green-600">${session.cash_sales?.toFixed(2) || '0.00'}</div>
            </div>
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="text-sm text-muted-foreground">Drops & Payouts</div>
              <div className="text-xl font-bold text-amber-600">
                -${((session.drops || 0) + (session.payouts || 0)).toFixed(2)}
              </div>
            </div>
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="text-sm text-muted-foreground">Expected Cash</div>
              <div className="text-xl font-bold text-foreground">${session.expected_cash?.toFixed(2)}</div>
            </div>
          </div>

          {/* Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Cash Drop */}
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <ArrowDownRight className="w-4 h-4 text-amber-600" />
                <span className="font-medium text-foreground">Cash Drop</span>
              </div>
              <Input
                type="number"
                placeholder="Amount"
                value={dropAmount}
                onChange={(e) => setDropAmount(e.target.value)}
                className="mb-2"
              />
              <Input
                placeholder="Notes (optional)"
                value={dropNotes}
                onChange={(e) => setDropNotes(e.target.value)}
                className="mb-2"
              />
              <Button onClick={recordDrop} size="sm" variant="outline" className="w-full">
                Record Drop
              </Button>
            </div>

            {/* Payout */}
            <div className="bg-muted/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Minus className="w-4 h-4 text-red-600" />
                <span className="font-medium text-foreground">Payout</span>
              </div>
              <Input
                type="number"
                placeholder="Amount"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                className="mb-2"
              />
              <Input
                placeholder="Description"
                value={payoutDesc}
                onChange={(e) => setPayoutDesc(e.target.value)}
                className="mb-2"
              />
              <Button onClick={recordPayout} size="sm" variant="outline" className="w-full">
                Record Payout
              </Button>
            </div>

            {/* Close Till */}
            <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-red-600" />
                <span className="font-medium text-foreground">Close Till</span>
              </div>
              <Input
                type="number"
                placeholder="Actual cash count"
                value={actualCash}
                onChange={(e) => setActualCash(e.target.value)}
                className="mb-2"
              />
              <Input
                placeholder="Notes (optional)"
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                className="mb-2"
              />
              <Button onClick={closeTill} size="sm" variant="destructive" className="w-full">
                Close & Count
              </Button>
            </div>
          </div>

          {/* Recent Transactions */}
          {session.transactions?.length > 0 && (
            <div>
              <h4 className="font-medium text-foreground mb-3">Recent Transactions</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {session.transactions.map((tx) => (
                  <div key={`tx-${tx.type}-${tx.description}-${tx.amount}`} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      {tx.type === 'sale' && <Plus className="w-4 h-4 text-green-600" />}
                      {tx.type === 'drop' && <ArrowDownRight className="w-4 h-4 text-amber-600" />}
                      {tx.type === 'payout' && <Minus className="w-4 h-4 text-red-600" />}
                      <div>
                        <div className="text-sm font-medium text-foreground capitalize">{tx.type}</div>
                        <div className="text-xs text-muted-foreground">{tx.description}</div>
                      </div>
                    </div>
                    <div className={`font-mono font-bold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.amount >= 0 ? '+' : ''}${Math.abs(tx.amount).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-heading text-lg text-foreground mb-4">Open Till</h3>
          <div className="max-w-md">
            <label className="block text-sm text-muted-foreground mb-2">Opening Float</label>
            <Input
              type="number"
              placeholder="200.00"
              value={openingFloat}
              onChange={(e) => setOpeningFloat(e.target.value)}
              className="mb-4"
            />
            <Button onClick={openTill} className="w-full">
              <Wallet className="w-4 h-4 mr-2" />
              Open Till
            </Button>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {summary && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-heading text-lg text-foreground mb-4">7-Day Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">{summary.total_sessions}</div>
              <div className="text-sm text-muted-foreground">Sessions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{summary.sessions_balanced}</div>
              <div className="text-sm text-muted-foreground">Balanced</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{summary.sessions_over}</div>
              <div className="text-sm text-muted-foreground">Over</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{summary.sessions_short}</div>
              <div className="text-sm text-muted-foreground">Short</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${summary.total_variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${Math.abs(summary.total_variance || 0).toFixed(2)}
              </div>
              <div className="text-sm text-muted-foreground">Total Variance</div>
            </div>
          </div>
        </div>
      )}

      {/* Recent History */}
      {history.length > 0 && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-heading text-lg text-foreground mb-4">Recent Sessions</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 px-3 text-sm text-muted-foreground">Date</th>
                  <th className="text-left py-2 px-3 text-sm text-muted-foreground">Opened By</th>
                  <th className="text-left py-2 px-3 text-sm text-muted-foreground">Expected</th>
                  <th className="text-left py-2 px-3 text-sm text-muted-foreground">Actual</th>
                  <th className="text-left py-2 px-3 text-sm text-muted-foreground">Variance</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 10).map((s) => (
                  <tr key={`history-${s.opened_at}`} className="border-b border-border">
                    <td className="py-2 px-3 text-sm text-foreground">
                      {new Date(s.opened_at).toLocaleDateString()}
                    </td>
                    <td className="py-2 px-3 text-sm text-muted-foreground">{s.opened_by}</td>
                    <td className="py-2 px-3 text-sm text-foreground">${s.expected_cash?.toFixed(2)}</td>
                    <td className="py-2 px-3 text-sm text-foreground">${s.actual_cash?.toFixed(2)}</td>
                    <td className={`py-2 px-3 text-sm font-medium ${
                      s.variance > 0 ? 'text-blue-600' : s.variance < 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {s.variance > 0 ? '+' : ''}{s.variance?.toFixed(2) || '0.00'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* POS Staff Cash Drawer Activity Log */}
      <div className="bg-card rounded-2xl border border-border p-6" data-testid="pos-drawer-activity-log">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <LockOpen className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="font-heading text-lg text-foreground">POS Drawer Activity</h3>
              <p className="text-sm text-muted-foreground">Staff cash drawer opens (non-order related)</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchPosDrawerLog}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Staff Breakdown Summary */}
        {staffBreakdown.length > 0 && (
          <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-200 dark:border-amber-800/30">
            <h4 className="text-sm font-medium text-amber-800 dark:text-amber-400 mb-3 flex items-center gap-2">
              <User className="w-4 h-4" />
              Manual Opens by Staff
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {staffBreakdown.map((staff) => (
                <div 
                  key={`staff-${staff.staff_id}`} 
                  onClick={() => setFilterStaff(filterStaff === staff.staff_id ? '' : staff.staff_id)}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${
                    filterStaff === staff.staff_id 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-white dark:bg-gray-800 hover:bg-amber-100 dark:hover:bg-amber-900/20'
                  }`}
                >
                  <div className="font-medium text-sm">{staff.staff_name}</div>
                  <div className={`text-2xl font-bold ${filterStaff === staff.staff_id ? 'text-white' : 'text-amber-600'}`}>
                    {staff.manual_opens}
                  </div>
                  <div className={`text-xs ${filterStaff === staff.staff_id ? 'text-amber-100' : 'text-muted-foreground'}`}>
                    manual opens
                  </div>
                </div>
              ))}
            </div>
            {filterStaff && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setFilterStaff('')}
                className="mt-2 text-amber-600"
              >
                Clear filter
              </Button>
            )}
          </div>
        )}

        {/* Activity Log Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 text-sm text-muted-foreground">Date/Time</th>
                <th className="text-left py-2 px-3 text-sm text-muted-foreground">Staff</th>
                <th className="text-left py-2 px-3 text-sm text-muted-foreground">Role</th>
                <th className="text-left py-2 px-3 text-sm text-muted-foreground">Reason</th>
              </tr>
            </thead>
            <tbody>
              {posDrawerLog.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-muted-foreground">
                    No drawer activity recorded yet
                  </td>
                </tr>
              ) : (
                posDrawerLog.map((log) => (
                  <tr key={`log-${log.timestamp}-${log.staff_id}`} className="border-b border-border hover:bg-muted/30">
                    <td className="py-2 px-3 text-sm text-foreground">
                      <div>{new Date(log.timestamp).toLocaleDateString()}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <div className="text-sm font-medium text-foreground">{log.staff_name}</div>
                      <div className="text-xs text-muted-foreground">{log.staff_id}</div>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        log.staff_role === 'owner' 
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                          : log.staff_role === 'cashier'
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                          : log.staff_role === 'server'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : log.staff_role === 'display'
                          ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                          : log.staff_role === 'manager' || log.staff_role === 'mgr-sup'
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
                      }`}>
                        {log.staff_role === 'manager' ? 'Manager (Legacy)' : log.staff_role === 'mgr-sup' ? 'Mgr-Sup' : log.staff_role}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        log.reason === 'manual' 
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                          : log.reason === 'order'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400'
                      }`}>
                        {log.reason === 'manual' ? 'Manual Open' : log.reason}
                      </span>
                      {log.order_id && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          Order #{log.order_id}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
